import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, Crown, UserRound, Lock, RefreshCw, Wallet } from "lucide-react";
import { useApp } from "../context/AppContext";
import { inr2 } from "../lib/format";
import { useToast } from "../hooks/use-toast";
import { Button } from "./ui/button";
import AuthModal from "./AuthModal";
import AdminGate from "./AdminGate";
import {
  DropdownMenu, DropdownMenuContent,
  DropdownMenuLabel, DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const LINKS = [];

export default function Navbar() {
  const { isAuthed, user, balance, onBet, refreshBalance } = useApp();
  const { toast } = useToast();
  const [refreshing, setRefreshing] = useState(false);
  const onRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try { await refreshBalance(); } catch { toast({ title: "Couldn't refresh. Try again." }); } finally { setRefreshing(false); }
  };
  const showBonusNudge = isAuthed && Number(user?.pendingSignupBonus) > 0;
  const amtLen = Math.max(inr2(balance).length, inr2(onBet).length);
  const amtCls = amtLen > 13 ? "text-[8px]" : amtLen > 10 ? "text-[9px]" : "text-[10.5px]";
  const [authOpen, setAuthOpen] = useState(false);
  const [authView, setAuthView] = useState("login");
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (isAuthed || new URLSearchParams(location.search).get("auth") !== "login") return;
    setAuthView("login");
    setAuthOpen(true);
    navigate(location.pathname, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, isAuthed]);
  const tapRef = React.useRef({ count: 0, timer: null });
  const [gateOpen, setGateOpen] = useState(false);

  // Secret admin entry: tap the top-left home icon 10 times, then enter the 8-digit passcode.
  const handleLogoTap = () => {
    const t = tapRef.current;
    clearTimeout(t.timer);
    t.count += 1;
    if (t.count >= 10) {
      t.count = 0;
      setGateOpen(true);
      return;
    }
    t.timer = setTimeout(() => { t.count = 0; }, 1500);
  };

  const isActive = (to) => {
    const path = to.split("?")[0];
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <header className="bbw-header sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-2 max-[359px]:px-1 sm:px-6 h-16 flex items-center justify-between gap-1.5 max-[359px]:gap-1">
        {/* LEFT: home + deposit (authed) or logo (guest) */}
        <div className="flex items-center gap-1.5 max-[359px]:gap-1 sm:gap-2 shrink-0">
          {isAuthed ? (
            <>
              <Link
                to="/"
                data-testid="header-home"
                aria-label="Home"
                className="grid place-items-center h-9 w-9 rounded-full bg-gradient-to-br from-[#f5b400] to-[#b07a00] text-black shrink-0 shadow-[0_0_14px_rgba(245,180,0,0.45)] hover:brightness-110 active:scale-95 transition-[filter,transform]"
              >
                <Home className="h-5 w-5" />
              </Link>
              <Link
                to="/wallet?tab=deposit"
                data-testid="header-deposit"
                className="relative flex items-center gap-1.5 rounded-full bg-white px-3 max-[359px]:px-2 sm:px-4 py-1.5 text-xs sm:text-sm font-extrabold shrink-0 ring-1 ring-[#E0B84C]/70 shadow-[0_0_12px_rgba(224,184,76,0.35)] hover:ring-[#E0B84C] hover:shadow-[0_0_18px_rgba(224,184,76,0.55)] transition-shadow"
              >
                <span className="whitespace-nowrap text-[#B8860B]">Deposit</span>
                {showBonusNudge && (
                  <span
                    className="absolute -top-1.5 -right-1.5 rounded-full bg-primary text-black text-[9px] font-extrabold uppercase tracking-wide px-1.5 py-0.5 ring-2 ring-background shadow-md animate-pulse"
                    data-testid="deposit-bonus-nudge"
                    title="Unlock your welcome bonus on your first deposit"
                  >
                    Bonus
                  </span>
                )}
              </Link>
            </>
          ) : (
            <button
              type="button"
              onClick={handleLogoTap}
              className="flex items-center gap-2 shrink-0 min-w-0"
              data-testid="nav-home"
              aria-label="Home"
            >
              <span className="h-9 w-9 rounded-full bg-gradient-to-br from-[#f5b400] to-[#b07a00] grid place-items-center shrink-0 shadow-[0_0_14px_rgba(245,180,0,0.45)]">
                <Home className="h-5 w-5 text-black" />
              </span>
            </button>
          )}

        <nav className="hidden lg:flex items-center gap-1">
          {LINKS.filter((l) => !l.auth || isAuthed).map((l) => (
            <Link
              key={l.label}
              to={l.to}
              data-testid="nav-deposit"
              className={`px-5 py-2.5 rounded-lg text-base font-bold transition-colors ${
                isActive(l.to) ? "text-primary bg-primary/15" : "text-primary bg-primary/10 hover:bg-primary/20"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>
        </div>

        {/* RIGHT: wallet + avatar (authed) or auth buttons (guest) */}
        <div className="flex items-center gap-1.5 max-[359px]:gap-1 sm:gap-2 shrink-0 ml-auto">
          {isAuthed ? (
            <>
              <div data-testid="wallet-balance-display" className="flex items-center gap-1 text-[#F5B800] leading-none select-none shrink-0 whitespace-nowrap">
                <Wallet className="h-5 w-5 shrink-0" data-testid="wallet-icon" />
                <div className={`grid grid-cols-[auto_auto] gap-x-1 gap-y-[3px] items-baseline font-bold ${amtCls}`}>
                  <span className="text-white">Main</span>
                  <span className="text-white tabular-nums text-right" data-testid="wallet-main-amount">{inr2(balance)}</span>
                  <span className="text-[#22C55E]">Bet</span>
                  <span className="text-[#22C55E] tabular-nums text-right" data-testid="wallet-onbet-amount">{inr2(onBet)}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={onRefresh}
                disabled={refreshing}
                aria-label="Refresh balance"
                data-testid="wallet-refresh-btn"
                className="bbw-btn-dark grid place-items-center h-[33px] w-[33px] rounded-[8px] text-[#F5B800] shrink-0 disabled:opacity-70"
              >
                <RefreshCw className={`h-[15px] w-[15px] ${refreshing ? "animate-spin" : ""}`} />
              </button>
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <button className={`flex items-center rounded-full p-1 transition-colors shrink-0 ${user?.vip ? "bg-gradient-to-r from-[#E0B84C]/25 to-amber-500/10 ring-1 ring-[#E0B84C]/60 hover:from-[#E0B84C]/35" : "bg-primary/10 ring-1 ring-primary/30 hover:bg-primary/15"}`} data-testid="avatar-btn">
                    <span className="relative shrink-0">
                      <span className={`h-7 w-7 rounded-full grid place-items-center font-semibold text-sm ${user?.vip ? "bg-gradient-to-br from-[#E0B84C] to-amber-600 text-black ring-2 ring-[#E0B84C]/50" : "bg-primary/25 text-primary"}`}>
                        {(user?.username || user?.name || "U").charAt(0).toUpperCase()}
                      </span>
                      {user?.vip && (
                        <span className="absolute -top-2 -right-1.5 rotate-12" data-testid="avatar-vip-crown" title="VIP member">
                          <Crown className="h-3.5 w-3.5 text-[#E0B84C] drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)] fill-[#E0B84C]" />
                        </span>
                      )}
                    </span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="font-semibold">@{user?.username || user?.phone}</div>
                    {user?.referralCode && (
                      <div className="mt-1.5 flex items-center gap-1 text-[11px]">
                        <span className="text-muted-foreground">Code:</span>
                        <span className="font-mono font-bold text-primary tracking-wider">{user.referralCode}</span>
                      </div>
                    )}
                  </DropdownMenuLabel>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <div className="flex items-center gap-2 shrink-0">
              <Button
                onClick={() => { setAuthView("register"); setAuthOpen(true); }}
                data-testid="nav-signup-btn"
                className="h-9 rounded-md font-extrabold uppercase tracking-wide text-black border-0 bg-[#f5b400] hover:bg-[#ffc21a] px-2.5 gap-1"
              >
                <UserRound className="h-4 w-4" /> Sign up
              </Button>
              <Button
                onClick={() => { setAuthView("login"); setAuthOpen(true); }}
                data-testid="nav-signin-btn"
                className="h-9 rounded-md font-extrabold uppercase tracking-wide text-white border-0 bg-[#d81b1b] hover:bg-[#ef2b2b] px-2.5 gap-1"
              >
                <Lock className="h-4 w-4" /> Login
              </Button>
            </div>
          )}
        </div>
      </div>

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} initialView={authView} />
      <AdminGate open={gateOpen} onOpenChange={setGateOpen} onUnlock={() => { setGateOpen(false); navigate("/admin"); }} />
    </header>
  );
}
